import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { AnalysisResult, ApiClient } from '../types';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface ChatComponentProps {
  result: AnalysisResult;
  apiClient: ApiClient;
  onUpdateHistoryChat?: (item: AnalysisResult, chat: Array<{ role: string; content: string }>) => void;
}

const ChatComponent: React.FC<ChatComponentProps> = ({ result, apiClient, onUpdateHistoryChat }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Generate storage key for the current result
  const getStorageKey = (res: AnalysisResult): string => {
    if (res.timestamp) return `chat:${res.timestamp}`;
    if (res.fileUrls?.[0]) return `chat:${res.fileUrls[0]}`;
    return `chat:${res.category}:${res.processingTime ?? 'unknown'}`;
  };

  // Normalize loaded messages to ensure type safety
  const normalizeMessages = (arr: unknown): Message[] => {
    const allowed = new Set(['user', 'assistant', 'system']);
    if (!Array.isArray(arr)) return [];
    
    return arr.map((x) => {
      const obj = (typeof x === 'object' && x !== null) ? (x as Record<string, unknown>) : {};
      const rawRole = typeof obj.role === 'string' ? obj.role : 'assistant';
      const role: Message['role'] = allowed.has(rawRole) ? (rawRole as Message['role']) : 'assistant';
      const content = obj.content != null ? String(obj.content) : '';
      return { role, content };
    });
  };

  // Persist messages to both sessionStorage and parent component
  const persistMessages = (msgs: Message[]) => {
    try {
      const key = getStorageKey(result);
      sessionStorage.setItem(key, JSON.stringify(msgs));
      
      if (onUpdateHistoryChat) {
        const payload = msgs.map((m) => ({ role: m.role, content: m.content }));
        onUpdateHistoryChat(result, payload);
      }
    } catch (err) {
      console.warn('Failed to persist messages:', err);
    }
  };

  // Load messages when result changes
  useEffect(() => {
    if (!result) {
      setMessages([]);
      return;
    }

    const systemMessage: Message = { role: 'system', content: `Transcript: ${result.analysis}` };

    // Check if result already has chat attached (from history)
    if (Array.isArray(result.chat) && result.chat.length > 0) {
      const msgs = normalizeMessages(result.chat);
      setMessages([systemMessage, ...msgs.filter((m) => m.role !== 'system')]);
      return;
    }

    // Try loading from sessionStorage
    try {
      const key = getStorageKey(result);
      const raw = sessionStorage.getItem(key);
      
      if (raw) {
        const parsed = normalizeMessages(JSON.parse(raw));
        setMessages([systemMessage, ...parsed.filter((m) => m.role !== 'system')]);
        return;
      }
    } catch (err) {
      console.warn('Failed to load chat from storage:', err);
    }

    // Start fresh with just the system message
    setMessages([systemMessage]);
  }, [result]);

  // Auto-scroll to bottom when messages change (only if near bottom)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 120) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // Persist messages whenever they change
  useEffect(() => {
    if (!result || messages.length === 0) return;
    persistMessages(messages);
  }, [messages, result]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text) return;

    const userMessage: Message = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      if (!apiClient.queryTranscript) {
        throw new Error('queryTranscript API not available');
      }

      const response = await apiClient.queryTranscript({
        messages: [...messages, userMessage],
        transcript: result.analysis,
        category: result.category,
      });

      const assistantMessage: Message = { role: 'assistant', content: response.response };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Chat error:', err);
      const errorMessage: Message = { 
        role: 'assistant', 
        content: 'Sorry, there was an error processing your query.' 
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="bg-bg-secondary border border-border-secondary rounded-xl p-4 shadow-sm flex-1 flex flex-col relative min-h-0">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-md font-semibold">Transcription Chat</h4>
          <span className="text-xs text-text-tertiary">{result.fileCount ?? 1} file(s) loaded</span>
        </div>

        {/* Messages container */}
        <div
          ref={containerRef}
          className="flex flex-col gap-3 flex-1 overflow-y-auto pr-1 min-h-0"
          aria-live="polite"
          tabIndex={0}
        >
          {messages
            .filter((m) => m.role !== 'system')
            .map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[90%] break-words leading-relaxed text-sm ${
                  m.role === 'user' ? 'text-brand-yellow' : 'text-text-primary'
                }`}>
                  {m.role === 'assistant' ? (
                    <ReactMarkdown className="prose prose-sm prose-invert max-w-none">
                      {m.content}
                    </ReactMarkdown>
                  ) : (
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  )}
                </div>
              </div>
            ))}

          {/* Loading indicator */}
          {loading && (
            <div className="flex justify-start">
              <div className="flex items-center gap-3 text-text-tertiary">
                <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                <span className="text-sm">Assistant is typing…</span>
              </div>
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="sticky bottom-0 bg-transparent pt-3 z-10">
          <div className="w-full bg-bg-primary border border-border-secondary rounded-xl px-3 py-2 flex items-center gap-2">
            <textarea
              aria-label="Ask a question about the transcript"
              placeholder="Ask a question about the transcript..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              style={{ maxHeight: '140px', minHeight: '40px' }}
              className="flex-1 resize-none bg-transparent border-none text-text-primary placeholder-text-tertiary text-sm leading-relaxed focus:outline-none"
              disabled={loading}
            />

            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              aria-label="Send query"
              className="p-2 bg-brand-yellow text-black rounded-md hover:bg-brand-yellow-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              title="Send"
            >
              <svg 
                className="w-4 h-4" 
                viewBox="0 0 36 36" 
                fill="currentColor" 
                xmlns="http://www.w3.org/2000/svg" 
                aria-hidden 
                stroke="currentColor" 
                strokeWidth="1"
              >
                <path d="M27.66,15.61,18,6,8.34,15.61A1,1,0,1,0,9.75,17L17,9.81V28.94a1,1,0,1,0,2,0V9.81L26.25,17a1,1,0,0,0,1.41-1.42Z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatComponent;